import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/lib/useSubscription";
import useHeartbeat from "@/lib/useHeartbeat";
import useUnseenOrders from "@/lib/useUnseenOrders";
import AuthImg from "./AuthImg";
import MobileBottomBar from "./MobileBottomBar";
import LanguageSwitcher from "./LanguageSwitcher";
import SwissBadge from "./SwissBadge";
import NotificationBell from "./NotificationBell";
import { TeamChatWidget } from "./TeamChatWidget";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Settings as SettingsIcon,
  LogOut,
  ShieldCheck,
  Menu,
  X,
  ChevronDown,
  User as UserIcon,
  Sliders,
  FileText as FileIcon,
  ShoppingBag,
  Megaphone,
  Lock,
  GraduationCap,
  LifeBuoy,
  Lightbulb,
  BookOpen,
  Accessibility,
  Scale,
  Banknote,
  ListChecks,
  FolderKanban,
  Calendar as CalendarIcon,
  Receipt,
  Mail as MailIcon,
  Camera,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AppTour } from "@/components/AppTour";

const NAV = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/kunden", labelKey: "nav.customers", icon: Users, testid: "nav-kunden", hideForRoles: ["admin", "backoffice"] },
  { to: "/aufgaben", labelKey: "nav.tasks", icon: ListChecks, testid: "nav-aufgaben", hideForRoles: ["admin"] },
  { to: "/termine", labelKey: "nav.appointments", icon: CalendarIcon, testid: "nav-termine", hideForRoles: ["admin"] },
  { to: "/buchhaltung", labelKey: "nav.accounting", icon: Banknote, testid: "nav-buchhaltung", hideForRoles: ["admin", "mitarbeiter"] },
  { to: "/users", labelKey: "nav.users", icon: Users, roles: ["admin", "backoffice"], testid: "nav-users" },
  { to: "/partner-auftraege", labelKey: "nav.partner_orders", icon: Camera, roles: ["admin"], testid: "nav-partner-auftraege" },
  { to: "/admin/partner", labelKey: "nav.partner_directory", icon: Megaphone, roles: ["admin"], testid: "nav-admin-partner" },
  { to: "/billing", labelKey: "nav.subscriptions", icon: CreditCard, testid: "nav-billing", hideForRoles: ["admin", "mitarbeiter", "backoffice"] },
  { to: "/shop", labelKey: "nav.shop_marketing", icon: ShoppingBag, testid: "nav-shop", premium: true },
  { to: "/admin/mail", labelKey: "nav.admin_mail", icon: MailIcon, roles: ["admin", "backoffice"], testid: "nav-admin-mail" },
  { to: "/admin/system", labelKey: "nav.db_export", icon: Sliders, roles: ["admin"], testid: "nav-admin-system"},
];

const ROLE_META = {
  admin: { label: "Admin", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  backoffice: { label: "Backoffice", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  gutachter: { label: "Gutachter", cls: "bg-[#12A9E8]/10 text-[#12A9E8] border-[#12A9E8]/30" },
  franchise: { label: "Franchise", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  provision: { label: "Partner", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

// Provisionspartner sehen nur ihren eigenen Bereich
const PROVISION_NAV = [
  { to: "/provision", labelKey: "nav.provision_orders", icon: Camera, testid: "nav-provision" },
];

export default function Layout({ children, title, subtitle, action, rightSlot, hideHeader, sidebarExtras, sidebarExtrasTitle, primaryAction }) {
  const { user, logout, accessToken } = useAuth();
  const { isActive, isAdmin } = useSubscription();
  useHeartbeat();
  const unseenOrders = useUnseenOrders();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  // Desktop sidebar: bleibt standardmäßig OFFEN (User-Vorgabe 2026-08-26) —
  // klappt nur ein, wenn der User sie aktiv wegklickt (Wahl wird gemerkt).
  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem("eg_sidebar_collapsed") === "1"
  );
  const toggleCollapsed = () => setCollapsed((v) => {
    const next = !v;
    localStorage.setItem("eg_sidebar_collapsed", next ? "1" : "0");
    return next;
  });
  const location = useLocation();

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const roleMeta = ROLE_META[user?.role] || { label: user?.role, cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
  // Alle Nav-Items sichtbar — Trial-User sehen die App komplett, damit sie
  // den Wert erkennen und ein Abo abschließen. Einzelne Funktionen zeigen
  // bei Bedarf einen Upsell-Dialog statt zu verstecken.
  const filteredNav = user?.role === "provision" ? PROVISION_NAV : NAV.filter((n) => {
    if (n.hideForRoles && n.hideForRoles.includes(user?.role)) return false;
    return !n.roles || n.roles.includes(user?.role) || user?.role === "admin";
  });

  return (
    <div className="min-h-screen bg-white flex" data-testid="app-layout">
      <TeamChatWidget />
      {/* Sidebar (desktop) — sticky damit sie beim Scrollen immer sichtbar bleibt */}
      <aside
        className={cn(
          "hidden lg:flex shrink-0 border-r border-zinc-200 bg-white flex-col transition-[width] duration-300 ease-out",
          "sticky top-0 self-start h-screen overflow-y-auto",
          collapsed ? "w-16" : "w-64"
        )}
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <div
          className={cn(
            "h-20 flex items-center transition-all",
            collapsed ? "justify-center px-2" : "justify-start pl-3 pr-6 gap-3"
          )}
        >
          <button
            onClick={toggleCollapsed}
            title={collapsed ? "Sidebar einblenden" : "Sidebar einklappen"}
            aria-label={collapsed ? "Sidebar einblenden" : "Sidebar einklappen"}
            data-testid="sidebar-toggle"
            className="inline-flex items-center justify-center h-10 w-10 rounded-lg text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 transition shrink-0"
          >
            <Menu className="h-5 w-5" strokeWidth={1.8} />
          </button>
          {!collapsed && (
            <Link to="/dashboard" data-testid="sidebar-brand" title="Easy Gutachter" className="min-w-0 flex items-center">
              <img
                src="/easy-gutachter-logo.png"
                alt="Easy Gutachter"
                className="h-16 w-auto select-none transition-all"
                draggable={false}
              />
            </Link>
          )}
        </div>

        <nav className={cn("flex-1 py-5 space-y-1", collapsed ? "px-2" : "px-3")} data-testid="sidebar-main-nav">
          {filteredNav.map((n) => {
            const locked = n.premium && !isActive && !isAdmin;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                title={collapsed ? t(n.labelKey) : locked ? "Mit Plan freischalten" : undefined}
                className={({ isActive: navActive }) =>
                  cn(
                    "flex items-center rounded-lg text-sm font-medium transition",
                    collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5",
                    navActive
                      ? "bg-[#12A9E8]/10 text-[#12A9E8] border border-[#12A9E8]/30"
                      : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50",
                    locked && "opacity-70"
                  )
                }
              >
                <span className="relative shrink-0">
                  <n.icon className="h-4 w-4" strokeWidth={1.75} />
                  {collapsed && n.to === "/dashboard" && unseenOrders > 0 && (
                    <span
                      data-testid="sidebar-orders-badge"
                      className="absolute -top-2 -right-2.5 min-w-[16px] h-[16px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow-[0_0_0_2px_white]"
                    >
                      {unseenOrders > 9 ? "9+" : unseenOrders}
                    </span>
                  )}
                </span>
                {!collapsed && (
                  <span className="truncate flex-1 flex items-center gap-1.5">
                    {t(n.labelKey)}
                    {locked && <Lock className="h-3 w-3 text-zinc-400 shrink-0" strokeWidth={2} />}
                  </span>
                )}
                {!collapsed && n.to === "/dashboard" && unseenOrders > 0 && (
                  <span
                    data-testid="sidebar-orders-badge"
                    className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                  >
                    {unseenOrders > 9 ? "9+" : unseenOrders}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Made in Switzerland — Sidebar-Footer */}
        <div className={cn("py-4 border-t border-zinc-100 flex", collapsed ? "justify-center px-2" : "px-3")}>
          <SwissBadge compact={collapsed} />
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <Link to="/dashboard" className="flex items-center" data-testid="drawer-brand">
                <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="h-12 w-auto select-none" draggable={false} />
              </Link>
              <button onClick={() => setMobileOpen(false)} data-testid="close-drawer"><X className="h-5 w-5" /></button>
            </div>
            <nav className="space-y-1">
              {filteredNav.map((n) => {
                const locked = n.premium && !isActive && !isAdmin;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive: navActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium",
                        navActive ? "bg-[#12A9E8]/10 text-[#12A9E8]" : "text-zinc-600 hover:bg-zinc-50",
                        locked && "opacity-70"
                      )
                    }
                  >
                    <n.icon className="h-4 w-4" />
                    <span className="flex-1">{t(n.labelKey)}</span>
                    {n.to === "/dashboard" && unseenOrders > 0 && (
                      <span
                        data-testid="drawer-orders-badge"
                        className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                      >
                        {unseenOrders > 9 ? "9+" : unseenOrders}
                      </span>
                    )}
                    {locked && <Lock className="h-3 w-3 text-zinc-400" strokeWidth={2} />}
                  </NavLink>
                );
              })}
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium",
                    isActive ? "bg-[#12A9E8]/10 text-[#12A9E8]" : "text-zinc-600 hover:bg-zinc-50"
                  )
                }
              >
                <SettingsIcon className="h-4 w-4" /> Einstellungen
              </NavLink>
            </nav>
            <div className="mt-auto pt-4 border-t border-zinc-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-full bg-zinc-100 flex items-center justify-center text-[13px] font-semibold text-zinc-700">
                  {user?.name?.slice(0, 2)?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 truncate">{user?.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
                </div>
              </div>
              <Badge variant="outline" className={cn("text-[10px] font-bold uppercase tracking-widest", roleMeta.cls)}>
                {roleMeta.label}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-3 justify-start text-rose-600"
                onClick={async () => { await logout(); navigate("/"); }}
              >
                <LogOut className="h-4 w-4 mr-2" /> Abmelden
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {!hideHeader && (
          <header className="sticky top-0 z-30 h-20 bg-white/90 backdrop-blur-md px-5 lg:px-10 flex items-center gap-4" data-testid="app-header">
            <button
              className={cn(
                "lg:hidden",
                // Gutachter/Franchise navigieren mobil über die Bottom-Tab-Bar
                // (nativer App-Look) — der Drawer bleibt nur für Admin-Rollen.
                !["admin", "backoffice", "mitarbeiter"].includes(user?.role) && "hidden"
              )}
              onClick={() => setMobileOpen(true)}
              data-testid="open-drawer"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              {typeof title === "string" || !title ? (
                <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-zinc-950 truncate">
                  {title || t("nav.dashboard")}
                </h1>
              ) : (
                title
              )}
              {subtitle && <p className="text-xs text-zinc-500 truncate">{subtitle}</p>}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* On mobile we hide the header action when a primaryAction is provided —
                  the FAB in the bottom bar replaces it. Desktop keeps both. */}
              <div className={cn(primaryAction && "hidden lg:flex", "items-center gap-2")}>
                {action}
                {rightSlot}
              </div>

              {/* Notification-Bell — direkt zwischen `Neue Akte` und Sprach-
                  Wechsler, wie vom User gewünscht. Roter Badge zeigt die
                  Anzahl ungelesener Nachrichten an, leichte Wackel-
                  Animation, wenn Updates vorliegen. */}
              <NotificationBell />

              {/* Sprach-Wechsler — nur für reguläre User (Gutachter/Franchise),
                  nicht im Admin/Backoffice-Bereich. */}
              {user?.role !== "admin" && user?.role !== "backoffice" && (
                <LanguageSwitcher />
              )}

              {/* User avatar + dropdown (desktop-prominent) */}
              <DropdownMenu>                <DropdownMenuTrigger asChild>
                  <button
                    className="group flex items-center gap-2 rounded-full border border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm pl-1 pr-2 py-1 transition ml-1"
                    data-testid="header-user-dropdown"
                    aria-label="Benutzermenü"
                  >
                    <div className="h-8 w-8 rounded-full overflow-hidden brand-gradient-bg text-white flex items-center justify-center text-[12px] font-semibold">
                      {user?.avatar_path ? (
                        <AuthImg path={user.avatar_path} token={accessToken} alt={user?.name || "Avatar"} className="w-full h-full object-cover" />
                      ) : (
                        user?.name?.slice(0, 2)?.toUpperCase() || <UserIcon className="h-4 w-4" />
                      )}
                    </div>
                    <div className="hidden sm:flex flex-col items-start leading-tight">
                      <span className="text-[13px] font-semibold text-zinc-900 max-w-[160px] truncate">{user?.name}</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{roleMeta.label}</span>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 text-zinc-400 group-hover:text-zinc-600" strokeWidth={2} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="user-dropdown-content">
                  <DropdownMenuLabel className="py-2">
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-full overflow-hidden brand-gradient-bg text-white flex items-center justify-center text-[13px] font-semibold">
                        {user?.avatar_path ? (
                          <AuthImg path={user.avatar_path} token={accessToken} alt={user?.name || "Avatar"} className="w-full h-full object-cover" />
                        ) : (
                          user?.name?.slice(0, 2)?.toUpperCase() || "U"
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-zinc-900 truncate">{user?.name}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className={cn("mt-2 text-[10px] font-bold uppercase tracking-widest", roleMeta.cls)}>
                      {roleMeta.label}
                    </Badge>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-dropdown-settings">
                    <Sliders className="h-4 w-4 mr-2" />
                    {t("nav.settings")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="user-dropdown-billing">
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t("nav.subscriptions")}
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/users")} data-testid="user-dropdown-users">
                      <Users className="h-4 w-4 mr-2" />
                      {t("nav.users")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/shop")} data-testid="user-dropdown-shop">
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    {t("nav.shop_marketing")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.dispatchEvent(new Event("eg:start-tour"))}
                    data-testid="user-dropdown-tour"
                  >
                    <GraduationCap className="h-4 w-4 mr-2" />
                    Tour starten
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/schulung")} data-testid="user-dropdown-schulung">
                    <GraduationCap className="h-4 w-4 mr-2" />
                    Schulung
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/support")} data-testid="user-dropdown-support">
                    <LifeBuoy className="h-4 w-4 mr-2" />
                    Support
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/verbesserung")} data-testid="user-dropdown-verbesserung">
                    <Lightbulb className="h-4 w-4 mr-2" />
                    Verbesserung
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/impressum")} data-testid="user-dropdown-impressum">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Impressum
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/datenschutz")} data-testid="user-dropdown-datenschutz">
                    <Scale className="h-4 w-4 mr-2" />
                    Datenschutz
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/barrierefreiheit")} data-testid="user-dropdown-barrierefreiheit">
                    <Accessibility className="h-4 w-4 mr-2" />
                    Barrierefreiheit
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => { await logout(); navigate("/"); }}
                    className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                    data-testid="user-dropdown-logout"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("common.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
        )}

        {/* Below-header flex row: optional secondary sidebar + main content */}
        <div className="flex-1 min-h-0 flex">
          {sidebarExtras && (
            <div className="hidden lg:flex w-72 shrink-0 justify-center pl-10">
              <aside
                className="flex w-60 flex-col sticky top-44 self-start max-h-[calc(100vh-12rem)] my-4 rounded-2xl bg-white shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)] ring-1 ring-zinc-100 overflow-hidden"
                data-testid="sidebar-secondary"
              >
                {sidebarExtrasTitle && (
                  <div className="px-5 py-4 border-b border-zinc-100">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-500">
                      {sidebarExtrasTitle}
                    </div>
                  </div>
                )}
                <div className="px-3 py-4 flex-1 overflow-y-auto" data-testid="sidebar-extras">
                  {sidebarExtras}
                </div>
              </aside>
            </div>
          )}
          <main className="flex-1 min-w-0 px-5 lg:px-10 py-6 lg:py-8 pb-24 lg:pb-10">{children}</main>
        </div>
      </div>
      <MobileBottomBar primaryAction={primaryAction} unseenCount={unseenOrders} />
      <AppTour />
    </div>
  );
}
